import {
  SUBSCRIPTION_KIND_BY_PROVIDER,
  SUBSCRIPTION_PRODUCT_NAMES,
  type ModelProvider,
  type ModelProviderSecret,
} from '@reflex/shared';

/**
 * Human label for a subscription-type secret, keyed by the provider it
 * authenticates. Subscriptions are stored under an internal alias
 * (usually `default`), which is a lookup key rather than a user-facing
 * name — so the picker shows the product name (e.g. "Claude Max")
 * instead. API keys keep their user-chosen name.
 *
 * Read from the shared table the server names mirror rows with: a label that
 * drifted from it would no longer invert back to the row's alias.
 */
function subscriptionLabel(provider: ModelProvider): string | undefined {
  const kind = SUBSCRIPTION_KIND_BY_PROVIDER[provider];
  return kind ? SUBSCRIPTION_PRODUCT_NAMES[kind] : undefined;
}

/**
 * Display name for a model-provider secret. API keys render their stored
 * name. Subscriptions render their stored name too — the server now
 * stores the product name (e.g. "Claude Max") — but legacy rows keyed
 * under the bare `default` alias are upgraded to the product label so
 * they don't read as "default" in the picker.
 */
export function modelProviderSecretDisplayName(
  secret: Pick<ModelProviderSecret, 'type' | 'provider' | 'name'>,
): string {
  if (secret.type === 'subscription' && (secret.name === 'default' || secret.name.length === 0)) {
    return subscriptionLabel(secret.provider) ?? secret.name;
  }
  return secret.name;
}
